import { useEffect, useCallback, useRef, useState } from "react";
import { useSettings, SecuritySettings } from "./useSettings";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const WARNING_BEFORE_LOGOUT_MS = 60000; // 1 minute warning before logout

export function useSessionTimeout() {
  const { getSetting, isLoading } = useSettings();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showWarning, setShowWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleLogout = useCallback(async () => {
    setShowWarning(false);
    toast.info("Sessão encerrada por inatividade");
    await supabase.auth.signOut();
    window.location.href = "/auth";
  }, []);

  const clearAllTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningRef.current) {
      clearTimeout(warningRef.current);
      warningRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const resetTimer = useCallback(() => {
    if (isLoading) return;

    const security = getSetting<SecuritySettings>("security");
    if (!security?.logout_on_inactive || !security?.session_timeout) return;

    const timeoutMs = security.session_timeout * 60 * 1000;
    const warningTime = timeoutMs - WARNING_BEFORE_LOGOUT_MS;

    clearAllTimers();
    setShowWarning(false);

    // Warning timer
    if (warningTime > 0) {
      warningRef.current = setTimeout(() => {
        setShowWarning(true);
        setRemainingTime(WARNING_BEFORE_LOGOUT_MS / 1000);
        
        // Countdown
        countdownRef.current = setInterval(() => {
          setRemainingTime((prev) => {
            if (prev <= 1) {
              if (countdownRef.current) clearInterval(countdownRef.current);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }, warningTime);
    }

    // Logout timer
    timeoutRef.current = setTimeout(handleLogout, timeoutMs);
  }, [getSetting, isLoading, handleLogout, clearAllTimers]);

  const extendSession = useCallback(() => {
    setShowWarning(false);
    resetTimer();
    toast.success("Sessão renovada");
  }, [resetTimer]);

  useEffect(() => {
    if (isLoading) return;

    const security = getSetting<SecuritySettings>("security");
    if (!security?.logout_on_inactive) return;

    const events = ["mousedown", "mousemove", "keypress", "scroll", "touchstart"];

    const handleActivity = () => {
      if (!showWarning) {
        resetTimer();
      }
    };

    events.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Initial timer setup
    resetTimer();

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
      clearAllTimers();
    };
  }, [isLoading, getSetting, resetTimer, showWarning, clearAllTimers]);

  return {
    showWarning,
    remainingTime,
    extendSession,
    logout: handleLogout,
  };
}
